/**
 * Quiz Scoring Engine
 *
 * Evaluates quiz submissions, calculates scores, and determines results.
 */

import type { FormField, QuizSettings } from '../types/form';

export interface QuestionResult {
  fieldId: string;
  fieldName: string;
  userAnswer: unknown;
  correctAnswer: unknown;
  isCorrect: boolean;
  pointsEarned: number;
  maxPoints: number;
  explanation?: string;
}

export interface QuizResult {
  totalScore: number;
  maxScore: number;
  percentage: number;
  passed: boolean;
  passingScore: number;
  questionResults: QuestionResult[];
  message: string;
}

/**
 * Evaluate a quiz submission and calculate results
 */
export function evaluateQuiz(
  fields: FormField[],
  values: Record<string, unknown>,
  quizSettings: QuizSettings
): QuizResult {
  const questionResults: QuestionResult[] = [];
  let totalScore = 0;
  let maxScore = 0;

  // Get scorable fields (fields that have correct answers defined)
  const scorableFields = fields.filter(
    (field) =>
      field.properties.correctAnswer !== undefined &&
      !['section', 'divider', 'page_break', 'calculated'].includes(field.type)
  );

  for (const field of scorableFields) {
    const userAnswer = values[field.name];
    const correctAnswer = field.properties.correctAnswer;
    const points = field.properties.points ?? 1;

    maxScore += points;

    const { isCorrect, pointsEarned } = checkAnswer(
      field,
      userAnswer,
      correctAnswer,
      points
    );

    totalScore += pointsEarned;

    questionResults.push({
      fieldId: field.id,
      fieldName: field.name,
      userAnswer,
      correctAnswer,
      isCorrect,
      pointsEarned,
      maxPoints: points,
      explanation: field.properties.explanation,
    });
  }

  const percentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
  const passingScore = quizSettings.passingScore ?? 0;
  const passed = percentage >= passingScore;

  const message = passed
    ? quizSettings.passingMessage || 'Congratulations! You passed!'
    : quizSettings.failingMessage || 'Unfortunately, you did not pass. Try again!';

  return {
    totalScore,
    maxScore,
    percentage: Math.round(percentage * 10) / 10,
    passed,
    passingScore,
    questionResults,
    message,
  };
}

/**
 * Check if an answer is correct
 */
function checkAnswer(
  field: FormField,
  userAnswer: unknown,
  correctAnswer: unknown,
  maxPoints: number
): { isCorrect: boolean; pointsEarned: number } {
  // Handle empty/undefined answers
  if (userAnswer === undefined || userAnswer === null || userAnswer === '') {
    return { isCorrect: false, pointsEarned: 0 };
  }

  const caseSensitive = field.properties.caseSensitive ?? false;

  // Handle different field types
  switch (field.type) {
    case 'text':
    case 'textarea':
    case 'email':
    case 'url':
      return checkTextAnswer(
        String(userAnswer),
        correctAnswer as string | string[],
        caseSensitive,
        maxPoints
      );

    case 'number':
    case 'slider':
    case 'rating':
      return checkNumberAnswer(
        Number(userAnswer),
        correctAnswer as number | number[],
        maxPoints
      );

    case 'select':
    case 'radio':
      return checkSingleChoiceAnswer(
        String(userAnswer),
        correctAnswer as string,
        maxPoints
      );

    case 'checkbox':
      return checkMultipleChoiceAnswer(
        userAnswer as string[],
        correctAnswer as string[],
        field.properties.partialCredit ?? false,
        maxPoints
      );

    case 'date':
    case 'datetime':
      return checkDateAnswer(
        String(userAnswer),
        correctAnswer as string,
        maxPoints
      );

    default:
      // For other field types, do simple equality check
      return {
        isCorrect: userAnswer === correctAnswer,
        pointsEarned: userAnswer === correctAnswer ? maxPoints : 0,
      };
  }
}

/**
 * Check text-based answers (can accept multiple correct answers)
 */
function checkTextAnswer(
  userAnswer: string,
  correctAnswer: string | string[],
  caseSensitive: boolean,
  maxPoints: number
): { isCorrect: boolean; pointsEarned: number } {
  const normalize = (str: string) =>
    caseSensitive ? str.trim() : str.trim().toLowerCase();

  const userNormalized = normalize(userAnswer);

  if (Array.isArray(correctAnswer)) {
    // Multiple acceptable answers
    const isCorrect = correctAnswer.some(
      (answer) => normalize(answer) === userNormalized
    );
    return { isCorrect, pointsEarned: isCorrect ? maxPoints : 0 };
  }

  const isCorrect = normalize(correctAnswer) === userNormalized;
  return { isCorrect, pointsEarned: isCorrect ? maxPoints : 0 };
}

/**
 * Check numeric answers (can accept multiple correct values or a range)
 */
function checkNumberAnswer(
  userAnswer: number,
  correctAnswer: number | number[],
  maxPoints: number
): { isCorrect: boolean; pointsEarned: number } {
  if (isNaN(userAnswer)) {
    return { isCorrect: false, pointsEarned: 0 };
  }

  if (Array.isArray(correctAnswer)) {
    // Multiple acceptable answers or range [min, max]
    if (correctAnswer.length === 2) {
      // Could be a range - check if userAnswer is within
      const [min, max] = correctAnswer;
      const isCorrect = userAnswer >= min && userAnswer <= max;
      return { isCorrect, pointsEarned: isCorrect ? maxPoints : 0 };
    }
    const isCorrect = correctAnswer.includes(userAnswer);
    return { isCorrect, pointsEarned: isCorrect ? maxPoints : 0 };
  }

  const isCorrect = userAnswer === correctAnswer;
  return { isCorrect, pointsEarned: isCorrect ? maxPoints : 0 };
}

/**
 * Check single choice answers (radio, select)
 */
function checkSingleChoiceAnswer(
  userAnswer: string,
  correctAnswer: string,
  maxPoints: number
): { isCorrect: boolean; pointsEarned: number } {
  const isCorrect = userAnswer === correctAnswer;
  return { isCorrect, pointsEarned: isCorrect ? maxPoints : 0 };
}

/**
 * Check multiple choice answers (checkbox)
 * Supports partial credit for partially correct answers
 */
function checkMultipleChoiceAnswer(
  userAnswer: string[],
  correctAnswer: string[],
  partialCredit: boolean,
  maxPoints: number
): { isCorrect: boolean; pointsEarned: number } {
  if (!Array.isArray(userAnswer) || !Array.isArray(correctAnswer)) {
    return { isCorrect: false, pointsEarned: 0 };
  }

  const userSet = new Set(userAnswer);
  const correctSet = new Set(correctAnswer);

  // Count correct selections
  let correctSelections = 0;
  for (const answer of userAnswer) {
    if (correctSet.has(answer)) {
      correctSelections++;
    }
  }

  // Count incorrect selections (selected wrong answers)
  let incorrectSelections = 0;
  for (const answer of userAnswer) {
    if (!correctSet.has(answer)) {
      incorrectSelections++;
    }
  }

  // Check if perfectly correct
  const isCorrect =
    userSet.size === correctSet.size &&
    [...userSet].every((val) => correctSet.has(val));

  if (isCorrect) {
    return { isCorrect: true, pointsEarned: maxPoints };
  }

  if (partialCredit && correctAnswer.length > 0) {
    // Calculate partial credit
    // Points = (correct - wrong) / total correct answers * max points
    // Minimum 0 points
    const ratio = Math.max(0, correctSelections - incorrectSelections) / correctAnswer.length;
    const pointsEarned = Math.round(ratio * maxPoints * 10) / 10;
    return { isCorrect: false, pointsEarned };
  }

  return { isCorrect: false, pointsEarned: 0 };
}

/**
 * Check date answers
 */
function checkDateAnswer(
  userAnswer: string,
  correctAnswer: string,
  maxPoints: number
): { isCorrect: boolean; pointsEarned: number } {
  // Normalize dates to YYYY-MM-DD format for comparison
  const userDate = new Date(userAnswer).toISOString().split('T')[0];
  const correctDate = new Date(correctAnswer).toISOString().split('T')[0];

  const isCorrect = userDate === correctDate;
  return { isCorrect, pointsEarned: isCorrect ? maxPoints : 0 };
}

/**
 * Get the display-friendly correct answer for a field
 */
export function getCorrectAnswerDisplay(
  field: FormField,
  correctAnswer: unknown
): string {
  if (correctAnswer === undefined || correctAnswer === null) {
    return '';
  }

  // For choice fields, show the label instead of value
  if (['select', 'radio', 'checkbox'].includes(field.type) && field.options) {
    if (Array.isArray(correctAnswer)) {
      const labels = correctAnswer
        .map((val) => {
          const option = field.options?.find((opt) => opt.value === val);
          return option?.label || val;
        });
      return labels.join(', ');
    }

    const option = field.options.find((opt) => opt.value === correctAnswer);
    return option?.label || String(correctAnswer);
  }

  if (Array.isArray(correctAnswer)) {
    return correctAnswer.join(' or ');
  }

  return String(correctAnswer);
}

/**
 * Shuffle array using Fisher-Yates algorithm
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Randomize questions and/or options for quiz mode
 */
export function randomizeQuiz(
  fields: FormField[],
  randomizeQuestions: boolean,
  randomizeOptions: boolean
): FormField[] {
  let processedFields = [...fields];

  if (randomizeQuestions) {
    // Keep non-question fields (sections, dividers, page breaks) in place
    // Shuffle only actual question fields within each page/section
    const questionTypes = ['text', 'textarea', 'email', 'number', 'select', 'radio', 'checkbox', 'date', 'rating', 'slider'];

    // Group fields by pages
    const groups: FormField[][] = [];
    let currentGroup: FormField[] = [];

    for (const field of processedFields) {
      if (field.type === 'page_break') {
        if (currentGroup.length > 0) {
          groups.push(currentGroup);
        }
        groups.push([field]); // Page break as its own group
        currentGroup = [];
      } else {
        currentGroup.push(field);
      }
    }
    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    // Shuffle questions within each group
    processedFields = groups.flatMap((group) => {
      if (group.length === 1 && group[0].type === 'page_break') {
        return group;
      }

      const questions = group.filter((f) => questionTypes.includes(f.type));
      const nonQuestions = group.filter((f) => !questionTypes.includes(f.type));

      return [...nonQuestions, ...shuffleArray(questions)];
    });
  }

  if (randomizeOptions) {
    // Randomize options for choice fields
    processedFields = processedFields.map((field) => {
      if (['select', 'radio', 'checkbox'].includes(field.type) && field.options) {
        return {
          ...field,
          options: shuffleArray(field.options),
        };
      }
      return field;
    });
  }

  return processedFields;
}
